import React, { useState, useEffect } from 'react';
import CurrencySelector from './CurrencySelector';
import CryptoAssetsList from './CryptoAssetsList';
import ZakatSummary from './ZakatSummary';
import CalculationHistory from './CalculationHistory';
import { 
  getCryptoPrices, 
  getGoldPricePerGram, 
  SUPPORTED_CURRENCIES 
} from '../services/api';

// Main component for Zakat Calculator
const ZakatCalculator = () => {
  // State for selected currency
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  
  // State for crypto assets
  const [assets, setAssets] = useState([
    { crypto: 'bitcoin', amount: '' }
  ]);
  
  // State for calculation results
  const [calculationResults, setCalculationResults] = useState({
    portfolioValue: 0,
    nisabThreshold: 0,
    zakatDue: 0,
    isZakatPayable: false,
  });
  
  // State for loading status
  const [isLoading, setIsLoading] = useState(false);
  
  // State for error messages
  const [error, setError] = useState(null);
  
  // State for calculation history
  const [calculationHistory, setCalculationHistory] = useState([]);

  // Get currency symbol for the selected currency
  const getCurrencySymbol = () => {
    const currency = SUPPORTED_CURRENCIES.find(c => c.code === selectedCurrency);
    return currency ? currency.symbol : '$';
  };

  // Create a map of currency codes to symbols for history display
  const currencySymbolsMap = SUPPORTED_CURRENCIES.reduce((map, currency) => {
    map[currency.code] = currency.symbol;
    return map;
  }, {});

  // Handle currency change
  const handleCurrencyChange = (currency) => {
    setSelectedCurrency(currency);
  };

  // Handle asset change
  const handleAssetChange = (updatedAssets) => {
    setAssets(updatedAssets);
  };

  // Add a new asset
  const handleAddAsset = () => {
    setAssets([...assets, { crypto: '', amount: '' }]);
  };

  // Remove an asset
  const handleRemoveAsset = (index) => {
    const updatedAssets = [...assets];
    updatedAssets.splice(index, 1);
    setAssets(updatedAssets);
  };

  // Calculate Zakat
  const calculateZakat = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Filter out assets with empty values
      const validAssets = assets.filter(asset => 
        asset.crypto && asset.amount && !isNaN(parseFloat(asset.amount))
      );
      
      if (validAssets.length === 0) {
        setCalculationResults({
          portfolioValue: 0,
          nisabThreshold: 0,
          zakatDue: 0,
          isZakatPayable: false,
        });
        setIsLoading(false);
        return;
      }
      
      // Get crypto prices
      const coinIds = validAssets.map(asset => asset.crypto);
      const cryptoPrices = await getCryptoPrices(coinIds, selectedCurrency);
      
      // Get gold price for Nisab calculation
      const goldPrice = await getGoldPricePerGram(selectedCurrency);
      
      // Calculate portfolio value
      let portfolioValue = 0;
      validAssets.forEach(asset => {
        const price = cryptoPrices[asset.crypto][selectedCurrency.toLowerCase()];
        portfolioValue += price * parseFloat(asset.amount);
      });
      
      // Calculate Nisab threshold (85 grams of gold)
      const nisabThreshold = goldPrice.price_per_gram * 85;
      
      // Determine if Zakat is payable
      const isZakatPayable = portfolioValue >= nisabThreshold;
      
      // Calculate Zakat due (2.5% of portfolio value)
      const zakatDue = portfolioValue * 0.025;
      
      // Update calculation results
      const results = {
        portfolioValue,
        nisabThreshold,
        zakatDue,
        isZakatPayable,
      };
      
      setCalculationResults(results);
      
      // Add to calculation history if Zakat is payable
      if (isZakatPayable) {
        const historyItem = {
          date: new Date().toISOString(),
          currency: selectedCurrency,
          portfolioValue,
          nisabThreshold,
          zakatDue,
        };
        
        setCalculationHistory([historyItem, ...calculationHistory]);
      }
    } catch (err) {
      setError('Failed to calculate Zakat. Please check your inputs and try again.');
      console.error('Calculation error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Recalculate when currency or assets change
  useEffect(() => {
    calculateZakat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrency, assets]);

  return (
    <div className="zakat-calculator">
      <h2>Crypto Zakat Calculator</h2>
      
      <div className="calculator-container">
        <CurrencySelector 
          selectedCurrency={selectedCurrency} 
          onCurrencyChange={handleCurrencyChange} 
        />
        
        <CryptoAssetsList 
          assets={assets} 
          onAssetChange={handleAssetChange} 
          onAddAsset={handleAddAsset} 
          onRemoveAsset={handleRemoveAsset} 
        />
        
        <ZakatSummary 
          portfolioValue={calculationResults.portfolioValue} 
          nisabThreshold={calculationResults.nisabThreshold} 
          zakatDue={calculationResults.zakatDue} 
          isZakatPayable={calculationResults.isZakatPayable} 
          selectedCurrency={selectedCurrency}
          currencySymbol={getCurrencySymbol()}
          isLoading={isLoading}
          error={error}
        />
      </div>
      
      <CalculationHistory 
        history={calculationHistory} 
        currencySymbols={currencySymbolsMap} 
      />
    </div>
  );
};

export default ZakatCalculator;